/**
* @license Apache-2.0
*
* Copyright (c) 2020 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import { ndarray as dnannsumpw } from '@stdlib/blas/ext/base/dnannsumpw';
import Float64Array from '@stdlib/array/float64';


// VARIABLES //

var WORKSPACE = new Float64Array( 2 );


// MAIN //

/**
* Computes the arithmetic mean of a double-precision floating-point strided array, ignoring `NaN` values and using pairwise summation.
*
* @param {PositiveInteger} N - number of indexed elements
* @param {Float64Array} x - input array
* @param {integer} strideX - stride length
* @param {NonNegativeInteger} offsetX - starting index
* @returns {number} arithmetic mean
*
* @example
* import Float64Array from '@stdlib/array/float64';
*
* var x = new Float64Array( [ 2.0, 1.0, 2.0, -2.0, -2.0, 2.0, 3.0, 4.0, NaN, NaN ] );
*
* var v = dnanmeanpw( 5, x, 2, 1 );
* // returns 1.25
*/
function dnanmeanpw( N, x, strideX, offsetX ) {
	WORKSPACE[ 0 ] = 0.0;
	WORKSPACE[ 1 ] = 0;
	dnannsumpw( N, x, strideX, offsetX, WORKSPACE, 1, 0 );
	return WORKSPACE[ 0 ] / WORKSPACE[ 1 ];
}


// EXPORTS //

export default dnanmeanpw;
